import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { Course, CourseResponse, Element, ElementData, Event, Session } from '@app/core/models/courses.model';
import { ModalAssessmentExecutionService } from '@app/modules/assessments-manager/shared/modal-assessment-execution/modal-assessment-execution.service';
import { SIDENAV_END, SIDENAV_START, SidenavRef } from '@app/shared/modules/template/components/sidenav';
import { notNull, switchDelay } from '@app/shared/utils';
import { TranslateService } from '@ngx-translate/core';
import { isEmpty } from 'lodash';
import { BehaviorSubject, Observable, switchMap, take, tap } from 'rxjs';
import { SidenavLearningAnnexComponent } from '../../components/sidenav-learning-annex/sidenav-learning-annex.component';
import { SidenavLearningUnitResponse } from '../../components/sidenav-learning-unit/sidenav-learning-unit.component';
import { CourseResolverData } from '../../resolvers/course.resolver';
import { mapTypeToIcon } from '../../utils/type-mapper';

@Component({
  selector: 'app-showcase',
  templateUrl: './unit.component.html',
  standalone: false,
})
export class UnitComponent implements OnInit {
  private readonly _route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly _modalAssessmentExecutionService = inject(ModalAssessmentExecutionService);
  private readonly translateService = inject(TranslateService);
  private readonly sidenav = inject<Observable<SidenavRef>>(SIDENAV_START);
  private readonly sidenavEnd = inject<Observable<SidenavRef>>(SIDENAV_END);
  public data: CourseResolverData;
  public course: Course;
  public event: Event;
  public sessions: Session[];
  public element$: Observable<SidenavLearningUnitResponse>;
  public elementData = new BehaviorSubject<ElementData>(null);
  public hasAnnex = false;
  public isVitrine = false;
  public typeIcon = mapTypeToIcon;

  private courseResponse: CourseResponse;

  public ngOnInit(): void {
    this.data = this._route.snapshot.data.courseResolverData;

    this.courseResponse = this.data.courseResponse;
    this.isVitrine = this.courseResponse.vitrine;

    this.course = this.courseResponse.curso;
    this.event = this.courseResponse.evento;
    this.sessions = this.courseResponse.sessoes;

    this.element$ = this.sidenav.pipe(
      switchDelay(),
      takeUntilDestroyed(this.destroyRef),
      notNull(),
      switchMap((res) => res.componentInstance$),
      notNull(),
      tap((sideRef) =>
        sideRef?.elementData$?.pipe(takeUntilDestroyed(this.destroyRef), notNull()).subscribe((res) => {
          this.elementData.next(res.elementData);
          this.hasAnnex = !isEmpty(res.elementData.anexos);
          this.sidenavEnd.pipe(takeUntilDestroyed(this.destroyRef), notNull(), take(1)).subscribe((ref) => {
            ref.open({
              componentRef: SidenavLearningAnnexComponent,
              closedAnchor: !this.hasAnnex,
              opened: this.hasAnnex,
              data: { anexos: this.elementData.value.anexos },
            });
          });
          if (res.elementData.tipo === 'avaliacao') {
            this.onOpenQuiz(res.elementData.quiz_uuid, this.isVitrine, res.elementData.evento_id, res.elementData.slug);
          }
        }),
      ),
      notNull(),
      switchMap((res) => res.elementData$ as Observable<SidenavLearningUnitResponse>),
    );

    this.elementData.next(this.data.selectedElementData);
  }

  public get eventInfo() {
    return {
      evento_id: !this.isVitrine ? this.event?.id : undefined,
      trail: {
        slug: this.data.courseResponse.slug,
      },
    };
  }

  public mapTypeToLabel(element: Element): string {
    const type = element.tipo.toLowerCase();

    const typeMapping: Record<string, string> = {
      audio: this.translateService.instant('trilhas.audio'),
      avaliacao: this.translateService.instant('trilhas.avaliacao'),
      flip: 'Flip',
      imagem: this.translateService.instant('trilhas.imagem'),
      link: this.translateService.instant('trilhas.link'),
      texto: ' ',
      video: this.translateService.instant('trilhas.video'),
      embed: ' ',
    };

    return typeMapping[type] || 'Desconhecido';
  }

  public onChangeElement(element: Element) {
    this.sidenav
      .pipe(
        take(1),
        notNull(),
        switchMap((res) => res.componentInstance$),
        notNull(),
      )
      .subscribe((res) => {
        res.onChangeElementButton(element);
      });
  }

  public onOpenAnnex(annex: string): void {
    window.open(annex, '_blank');
  }

  private onOpenQuiz(quizUuid: string, vitrine: boolean, eventoId: number = null, elementoSlug: string = null) {
    this._modalAssessmentExecutionService.open({
      data: { aplicacao_uuid: quizUuid },
      extra: {
        preview: vitrine,
        trilha: {
          eventoId: eventoId || this.event?.id,
          trilhaSlug: this.data.courseResponse.slug,
          elementoSlug,
        },
      },
    });
  }
}
